import { abweisen, verlangen } from "../gemeinsam/index.js";
import { istWildart, type Wildart } from "../drueckjagd/oeffentlich.js";

/**
 * Wertobjekte des Kontexts Schuss (Domänenmodell §7, §11). Prüfungen werfen eine Abweisung mit dem Grund aus den
 * Feature Files `schuss/*` (befehle.md §7); was ein Wert in der Spezifikation nicht kennt, wird nie still angenommen.
 */

export const SCHUSSAUSGAENGE = ["Stück liegt", "Anschuss", "Fehlschuss"] as const;
export type Schussausgang = (typeof SCHUSSAUSGAENGE)[number];

/** Altersklassen bei Rot- und Damwild (Wertobjekt `Ansprache`); bei den übrigen Wildarten gibt es keine. */
export const ALTERSKLASSEN = ["Kalb", "Schmaltier", "Alttier", "Hirsch"] as const;
export type Altersklasse = (typeof ALTERSKLASSEN)[number];
export const WILDARTEN_MIT_ALTERSKLASSE: readonly Wildart[] = ["Rotwild", "Damwild"];

export const HIMMELSRICHTUNGEN = ["N", "NO", "O", "SO", "S", "SW", "W", "NW"] as const;
export type Himmelsrichtung = (typeof HIMMELSRICHTUNGEN)[number];

export const PIRSCHZEICHEN = ["Schweiß", "Schnitthaar", "Knochensplitter", "Wildbret", "Sonstiges"] as const;

export const QUELLEN_ANSCHUSSPOSITION = ["geschätzt aus Richtung und Entfernung", "auf der Karte gesetzt", "vor Ort gemessen"] as const;
export type QuelleAnschussposition = (typeof QUELLEN_ANSCHUSSPOSITION)[number];

export const QUELLEN_LIEGEORT = ["GPS-Messung", "Karte"] as const;
export type QuelleLiegeort = (typeof QUELLEN_LIEGEORT)[number];

export const NACHSUCHENERGEBNISSE = ["gefunden", "erlegt", "nicht gefunden"] as const;
export type Nachsuchenergebnis = (typeof NACHSUCHENERGEBNISSE)[number];

export const SUCHARTEN = ["Nachsuche", "Kontrollsuche"] as const;
export type Suchart = (typeof SUCHARTEN)[number];

/** Rollen, denen eine Suche zugewiesen werden kann (Invariante Schuss, UL §9-Reihenfolge). */
export const HUNDEFUEHRENDE_ROLLEN = ["Durchgeh-Hundeführer", "Standschnaller", "Nachsuchenführer"] as const;
/** Kennzeichen „bestätigter Nachsuchenführer“ folgt der Rolle Nachsuchenführer (F-151, Anforderungen K38). */
export const BESTAETIGTER_NACHSUCHENFUEHRER = "bestätigter Nachsuchenführer";

export interface Position {
  Breite: number;
  Länge: number;
}

const enthaelt = <T extends string>(liste: readonly T[], w: unknown): w is T => typeof w === "string" && (liste as readonly string[]).includes(w);

/** Ein Text ohne Inhalt gilt als nicht angegeben. */
export function textOderNull(w: unknown): string | null {
  return typeof w === "string" && w.trim() !== "" ? w.trim() : null;
}

/** Wildart und Altersklasse eines Schusses (Invarianten zur Ansprache, `schuss/02`). */
export function anspracheLesen(wildart: unknown, altersklasse: unknown): { wildart: Wildart; altersklasse: Altersklasse | null } {
  verlangen(textOderNull(wildart) !== null, "wildart-fehlt");
  verlangen(istWildart(wildart), "wildart-unbekannt");
  const ak = textOderNull(altersklasse);
  if (WILDARTEN_MIT_ALTERSKLASSE.includes(wildart)) {
    verlangen(ak !== null, "altersklasse-fehlt");
    verlangen(enthaelt(ALTERSKLASSEN, ak), "ansprache-unzulaessig");
    return { wildart, altersklasse: ak };
  }
  verlangen(ak === null, "ansprache-unzulaessig");
  return { wildart, altersklasse: null };
}

export function schussausgangLesen(w: unknown): Schussausgang {
  verlangen(textOderNull(w) !== null, "schussausgang-fehlt");
  verlangen(enthaelt(SCHUSSAUSGAENGE, w), "schussausgang-unbekannt");
  return w;
}

export function fluchtrichtungLesen(w: unknown): Himmelsrichtung | null {
  if (w === undefined || w === null) return null;
  verlangen(enthaelt(HIMMELSRICHTUNGEN, w), "fluchtrichtung-unbekannt");
  return w;
}

export function pirschzeichenLesen(w: unknown): string[] {
  const liste = Array.isArray(w) ? w : [w];
  for (const p of liste) verlangen(enthaelt(PIRSCHZEICHEN, p), "pirschzeichen-unbekannt");
  return [...new Set(liste as string[])];
}

/** Geoposition im WGS 84 (Wertobjekt `Geoposition`, N-48): Breite ±90°, Länge ±180°. */
export function positionLesen(w: unknown): Position {
  const p = w as { Breite?: unknown; Länge?: unknown } | null;
  const b = Number(p?.Breite);
  const l = Number(p?.Länge);
  if (typeof p !== "object" || p === null || !Number.isFinite(b) || !Number.isFinite(l) || Math.abs(b) > 90 || Math.abs(l) > 180) {
    abweisen("position-ungueltig");
  }
  return { Breite: b, Länge: l };
}

/** Genauigkeit einer Messung in Metern; ohne Angabe `null`. */
export function genauigkeitLesen(w: unknown): number | null {
  if (w === undefined || w === null) return null;
  verlangen(typeof w === "number" && Number.isFinite(w) && w >= 0, "unzulaessige-angaben");
  return w;
}

/** Anzahl abgegebener Schüsse (Vorgabe 1, Domänenmodell §7). */
export function anzahlSchuesseLesen(w: unknown): number {
  if (w === undefined || w === null) return 1;
  verlangen(typeof w === "number" && Number.isInteger(w) && w >= 1, "unzulaessige-angaben");
  return w;
}

export function quelleAnschusspositionLesen(w: unknown): QuelleAnschussposition {
  verlangen(enthaelt(QUELLEN_ANSCHUSSPOSITION, w), "unzulaessige-angaben");
  return w;
}

export function quelleLiegeortLesen(w: unknown): QuelleLiegeort {
  verlangen(enthaelt(QUELLEN_LIEGEORT, w), "unzulaessige-angaben");
  return w;
}

export function nachsuchenergebnisLesen(w: unknown): Nachsuchenergebnis {
  verlangen(enthaelt(NACHSUCHENERGEBNISSE, w), "nachsuchenergebnis-unbekannt");
  return w;
}

export function istHundefuehrend(rollen: readonly string[]): boolean {
  return rollen.some((r) => enthaelt(HUNDEFUEHRENDE_ROLLEN, r));
}

/** Ansprache als ein Text: Wildart, bei Rot- und Damwild mit Altersklasse (TESTSPRACHE §3, z. B. „Rotwild Kalb“). */
export function anspracheText(wildart: string | null, altersklasse: string | null): string | null {
  if (!wildart) return null;
  return altersklasse ? `${wildart} ${altersklasse}` : wildart;
}

const WINKEL: Record<Himmelsrichtung, number> = { N: 0, NO: 45, O: 90, SO: 135, S: 180, SW: 225, W: 270, NW: 315 };
/** Große Halbachse und erste Exzentrizität² des WGS-84-Ellipsoids. */
const A = 6378137.0;
const E2 = 6.69437999014e-3;

/**
 * Geschätzte Anschussposition (F-123, `schuss/03`): die Position des Standes, um die geschätzte Entfernung in der
 * geschätzten Himmelsrichtung verschoben, auf dem WGS-84-Ellipsoid (Meridian- und Querkrümmungsradius in der Breite
 * des Standes). Für Entfernungen bis zu wenigen hundert Metern liegt die Abweichung von der geodätischen Linie im
 * Millimeterbereich.
 */
export function anschusspositionSchaetzen(stand: Position, richtung: Himmelsrichtung, entfernungM: number): Position {
  const phi = (stand.Breite * Math.PI) / 180;
  const w = Math.sqrt(1 - E2 * Math.sin(phi) ** 2);
  const meridian = (A * (1 - E2)) / w ** 3;
  const quer = A / w;
  const winkel = (WINKEL[richtung] * Math.PI) / 180;
  const nord = entfernungM * Math.cos(winkel);
  const ost = entfernungM * Math.sin(winkel);
  return {
    Breite: stand.Breite + ((nord / meridian) * 180) / Math.PI,
    Länge: stand.Länge + ((ost / (quer * Math.cos(phi))) * 180) / Math.PI,
  };
}

export function richtungLesen(w: unknown): Himmelsrichtung {
  verlangen(enthaelt(HIMMELSRICHTUNGEN, w), "richtung-ungueltig");
  return w;
}

export function entfernungLesen(w: unknown): number {
  verlangen(typeof w === "number" && Number.isFinite(w) && w > 0, "unzulaessige-angaben");
  return w;
}
